import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ABOUT_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/about#webpage`,
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/about`,
      "name": "About DTPU – Licensed Electrical Contractor NYC Since 2008",
      "description": "Learn about Done to Power Up (DTPU), a licensed electrical contractor serving NYC and Westchester since 2008. Meet our team of expert electricians dedicated to safety, quality, and customer satisfaction.",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": import.meta.env.VITE_SITE_URL || "https://example.com" },
          { "@type": "ListItem", "position": 2, "name": "About", "item": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/about` }
        ]
      }
    },
    {
      "@type": "AboutPage",
      "name": "About Done to Power Up",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/about`,
      "description": "DTPU (Done To Power Up) is a trusted electrical contractor serving residential and commercial clients throughout New York City since 2008.",
      "mainEntity": {
        "@type": "ElectricalContractor",
        "name": "Done to Power Up (DTPU)",
        "foundingDate": "2008",
        "numberOfEmployees": { "@type": "QuantitativeValue", "value": 10 },
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "2568 17th Street, Suite 101",
          "addressLocality": "Brooklyn",
          "addressRegion": "NY",
          "postalCode": "11235",
          "addressCountry": "US"
        },
        "employee": [
          { "@type": "Person", "name": "Diego Navarro", "jobTitle": "Master Electrician" },
          { "@type": "Person", "name": "Carlos Rios", "jobTitle": "Field Supervisor" },
          { "@type": "Person", "name": "Alex Cedeno", "jobTitle": "Chief Administrator & Customer Support" }
        ]
      }
    }
  ]
};

const AboutPage = () => {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-about';
    script.textContent = JSON.stringify(ABOUT_SCHEMA);
    document.head.appendChild(script);
    document.title = 'About DTPU – Licensed Electrical Contractor NYC Since 2008';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Learn about Done to Power Up (DTPU), a licensed electrical contractor serving NYC and Westchester since 2008. Meet our expert electricians dedicated to safety, quality, and customer satisfaction.');
    return () => { document.getElementById('schema-about')?.remove(); };
  }, []);

  const values = [
    {
      number: '01',
      title: 'Safety First',
      description: 'Every project begins and ends with safety. We follow strict safety protocols and ensure all work meets or exceeds electrical codes and standards.',
    },
    {
      number: '02',
      title: 'Quality Workmanship',
      description: 'Our licensed electricians take pride in delivering exceptional work. We use premium materials and proven techniques to ensure lasting results.',
    },
    {
      number: '03',
      title: 'Customer Satisfaction',
      description: 'Your satisfaction is our priority. We communicate clearly, arrive on time, and stand behind our work with comprehensive warranties.',
    },
    {
      number: '04',
      title: 'Continuous Innovation',
      description: 'We stay current with the latest electrical technologies and techniques, bringing cutting-edge solutions to every project.',
    },
  ];

  const stats = [
    { number: '15+', label: 'Years Experience' },
    { number: '150+', label: 'Projects Completed' },
    { number: '100%', label: 'Licensed & Insured' },
    { number: '24/7', label: 'Emergency Service' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner */}
      <section className="relative pt-32 pb-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-2">
              <p className="text-primary text-xs font-bold tracking-[0.2em] uppercase mb-4">
                ABOUT DTPU
              </p>
              <h1 className="text-6xl font-bold text-secondary mb-6 leading-tight">
                Powering NYC Since 2008
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed font-serif">
                DTPU (Done To Power Up) is a trusted electrical contractor serving residential and commercial clients throughout New York City. Our mission is to deliver safe, reliable, and code-compliant electrical solutions that power your life and business.
              </p>
            </div>

            {/* Right Image */}
            <div className="lg:col-span-3">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/8b7d9b5f6d4ffeae2f83ada0571b5c77.jpg"
                  alt="DTPU Team"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-primary mb-2">
                  {stat.number}
                </div>
                <div className="text-white text-sm font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-24 bg-white">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-secondary mb-6">
              Our Mission
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              At DTPU, we're committed to providing exceptional electrical services that prioritize safety, quality, and customer satisfaction. Our team of licensed and insured electricians brings years of experience and expertise to every project, ensuring your electrical systems are safe, efficient, and built to last.
            </p>
          </div>

          {/* Icon Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-primary text-5xl"></i>
              </div>
              <h3 className="text-xl font-bold text-secondary mb-2">
                Licensed & Insured
              </h3>
              <p className="text-gray-600 text-sm">
                Fully licensed electricians in NYC and Westchester County with comprehensive insurance coverage for your protection
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-list-3-line text-primary text-5xl"></i>
              </div>
              <h3 className="text-xl font-bold text-secondary mb-2">
                Code Compliant
              </h3>
              <p className="text-gray-600 text-sm">
                All work meets or exceeds local and national electrical codes and standards
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-primary text-5xl"></i>
              </div>
              <h3 className="text-xl font-bold text-secondary mb-2">
                15+ Years Experience
              </h3>
              <p className="text-gray-600 text-sm">
                Decades of combined experience serving NYC residential and commercial clients
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-secondary mb-6">
              Our Core Values
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              These principles guide everything we do, from the smallest repair to the largest installation project.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl border-l-4 border-primary hover:shadow-lg transition-all duration-300 relative cursor-pointer"
              >
                <div className="text-6xl font-bold text-primary/20 absolute top-4 left-4">
                  {value.number}
                </div>
                <div className="relative pl-16">
                  <h3 className="text-2xl font-bold text-secondary mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-secondary mb-6">
              Meet Our Team
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our licensed electricians are the backbone of DTPU. Each team member brings specialized expertise, dedication to safety, and a commitment to exceptional service.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { name: 'Diego Navarro', role: 'Master Electrician', image: 'https://static.readdy.ai/image/538b4e65f17508bb0aa3f7f0e8fac3a5/712f67d2de8ed329cbe8df2c23331eae.png', isRealImage: true, objectPosition: 'object-[center_27%]' },
              { name: 'Carlos Rios', role: 'Field Supervisor', image: 'https://static.readdy.ai/image/538b4e65f17508bb0aa3f7f0e8fac3a5/947d3ca8b076a6ec6590b9d24a2285fd.jpeg', isRealImage: true, objectPosition: 'object-[center_15%]' },
              { name: 'Alex Cedeno', role: 'Chief Administrator & Customer Support', image: 'https://static.readdy.ai/image/538b4e65f17508bb0aa3f7f0e8fac3a5/19bf0720fbcd7c06229b8d05106a4968.jpeg', isRealImage: true, objectPosition: 'object-[center_20%]' },
            ].map((member, index) => (
              <Link key={index} to="/contact" className="text-center group cursor-pointer block">
                <div className="rounded-2xl overflow-hidden mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <img
                    src={member.isRealImage ? member.image : `https://readdy.ai/api/search-image?query=$%7Bmember.image%7D&width=280&height=320&seq=dtpu-team-blue-${index}&orientation=portrait`}
                    alt={member.name}
                    className={`w-full h-80 object-cover ${member.objectPosition} group-hover:scale-105 transition-transform duration-300`}
                  />
                </div>
                <h3 className="text-xl font-bold text-secondary mb-1">
                  {member.name}
                </h3>
                <p className="text-primary font-medium text-sm">
                  {member.role}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-blue-700">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Experience the DTPU Difference
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers who trust DTPU for their electrical needs. Contact us today for a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="bg-white text-primary px-8 py-4 rounded-full font-bold text-base hover:bg-gray-100 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
            >
              Get Started Today
            </Link>
            <Link
              to="/services"
              className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-base hover:bg-white hover:text-primary transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              View Our Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
